import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { analyzeReport } from '../../../services/openaiService';

const ReportAnalysisPanel = ({ reportData, reportTitle }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisType, setAnalysisType] = useState('summary');

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await analyzeReport(reportData, analysisType);
      setAnalysis(result);
    } catch (err) {
      setError(err?.message || 'Failed to analyze report');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'text-red-600 bg-red-50 border-red-200',
      medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      low: 'text-blue-600 bg-blue-50 border-blue-200',
    };
    return colors?.[priority] || colors?.medium;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">AI-Powered Report Analysis</h3>
        </div>
        {analysis && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <span>Confidence: {Math.round(analysis?.confidence * 100)}%</span>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <select
          value={analysisType}
          onChange={(e) => setAnalysisType(e?.target?.value)}
          className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
          disabled={loading}
        >
          <option value="summary">Executive Summary</option>
          <option value="insights">Key Insights</option>
          <option value="recommendations">Recommendations</option>
        </select>
        <Button
          onClick={handleAnalyze}
          disabled={loading}
          iconName={loading ? 'Loader2' : 'Sparkles'}
        >
          {loading ? 'Analyzing...' : 'Analyze'}
        </Button>
      </div>
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <Icon name="AlertCircle" size={16} color="#dc2626" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {analysis && (
        <div className="space-y-4 mt-4">
          {/* Summary */}
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-semibold text-foreground mb-2">Summary</h4>
            <p className="text-sm text-muted-foreground">{analysis?.summary}</p>
          </div>

          {/* Key Findings */}
          {analysis?.keyFindings?.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="FileText" size={16} />
                Key Findings
              </h4>
              <ul className="space-y-2">
                {analysis?.keyFindings?.map((finding, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Icon name="ChevronRight" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{finding}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Insights */}
          {analysis?.insights?.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="Lightbulb" size={16} />
                Insights
              </h4>
              <ul className="space-y-2">
                {analysis?.insights?.map((insight, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Icon name="TrendingUp" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {analysis?.recommendations?.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Target" size={16} />
                Recommendations
              </h4>
              <div className="space-y-3">
                {analysis?.recommendations?.map((rec, index) => (
                  <div
                    key={index}
                    className={`p-3 border rounded-lg ${getPriorityColor(rec?.priority)}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h5 className="font-semibold text-sm">{rec?.title}</h5>
                      <span className="text-xs uppercase font-semibold px-2 py-0.5 rounded">
                        {rec?.priority}
                      </span>
                    </div>
                    <p className="text-sm opacity-90">{rec?.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportAnalysisPanel;